import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { inspectionCheckStatusTextClass } from "./tenantDetailHelpers";
import SignaturePad from "./SignaturePad";

// Normalize a room id to either a non-empty string or null. Empty string / null /
// undefined all collapse to null (= "no room" = shared/common for an assignment,
// or "no target room recorded" for an inspection).
function normRoomId(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

// Effective persisted status of a checklist item (saved value drives progress
// and the Phase-2 status filter; unsaved drafts do not).
function statusOf(it) {
  return String(it?.check_status || "not_checked").toLowerCase();
}

// Phase 5.4b — snapshot-first display resolution. Prefer the frozen snapshot_*
// (stable for finalized protocols); fall back to the live unit assignment for
// legacy rows whose snapshot is NULL. Mirrors the PDF builder
// (inspection_protocol_pdf_data_builder.py) so modal and PDF show the same
// truth. snapshot_item_name is the anchor — snapshot_room_id/name are
// legitimately null for shared/common inventory.
function hasItemSnapshot(it) {
  return it?.snapshot_item_name != null;
}

function resolveItemDisplay(it, asg) {
  if (hasItemSnapshot(it)) {
    return {
      hasSnapshot: true,
      name: it.snapshot_item_name || "—",
      roomId: normRoomId(it.snapshot_room_id),
      roomName: it.snapshot_room_name ?? null,
      quantity: it.snapshot_quantity ?? "—",
    };
  }
  return {
    hasSnapshot: false,
    name: asg?.item_name || "—",
    roomId: normRoomId(asg?.room_id),
    roomName: asg?.room_name ?? null,
    quantity: asg?.quantity ?? "—",
  };
}

// Phase-2 status filter chips. "all" passes everything; the rest match one
// check_status. Order is iPad-tap-friendly: open first, then outcomes.
const STATUS_FILTERS = [
  { key: "all", label: "Alle", match: () => true },
  { key: "open", label: "Offen", match: (s) => s === "not_checked" },
  { key: "ok", label: "OK", match: (s) => s === "ok" },
  { key: "damaged", label: "Beschädigt", match: (s) => s === "damaged" },
  { key: "missing", label: "Fehlt", match: (s) => s === "missing" },
  { key: "not_applicable", label: "Nicht relevant", match: (s) => s === "not_applicable" },
];

// Best-effort short date/time for the finalized badge. finalized_at is an ISO-Z
// string from the backend; fall back to the raw value if it can't be parsed.
function formatFinalizedAt(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// inspection_date is a YYYY-MM-DD date string → DD.MM.YYYY (no Date/timezone shift).
function formatDateOnly(value) {
  if (!value) return "—";
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
}

export default function InspectionChecklistModal({
  modal,
  onClose,
  onSaveRow,
  onUploadPhotos,
  onDeletePhoto,
  onFinalize,
  onGenerateProtocolPdf,
  onOpenProtocolPdf,
  onSignDual,
  onOpenSignedPdf,
  unitLabelShort,
  // Display name of the logged-in admin, resolved by the parent page (this
  // component stays presentational: props in, no API, no context).
  currentUserName = "",
}) {
  const {
    loading,
    err,
    invType,
    inspection,
    unitAssignments,
    savingItemId,
    savedItemId,
    uploadingPhotoItemId,
    finalizing,
    generatingProtocolPdf,
    openingProtocolPdf,
    signing,
    openingSignedPdf,
    tenancy,
  } = modal || {};
  const [drafts, setDrafts] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");

  // Dual signature (ÜP-S3): tenant + management/landlord representative co-sign.
  const [signOpen, setSignOpen] = useState(false);
  const [signaturePlace, setSignaturePlace] = useState("");
  const [tenantSignerName, setTenantSignerName] = useState("");
  const [managementSignerName, setManagementSignerName] = useState("");
  // Per-field validation messages, keyed by field. Empty object = no errors.
  // Populated ONLY on an actual submit attempt (never while typing), and cleared
  // per field as soon as the user corrects that field.
  const [signErrors, setSignErrors] = useState({});
  const tenantSigPadRef = useRef(null);
  const managementSigPadRef = useRef(null);
  // Focus/scroll targets for the first invalid field on a failed submit.
  const signaturePlaceRef = useRef(null);
  const tenantNameRef = useRef(null);
  const tenantSigBoxRef = useRef(null);
  const managementNameRef = useRef(null);
  const managementSigBoxRef = useRef(null);

  useEffect(() => {
    if (!inspection?.items?.length) {
      setDrafts({});
      return;
    }
    const next = {};
    for (const it of inspection.items) {
      next[it.id] = {
        check_status: String(it.check_status || "not_checked").toLowerCase(),
        condition_note: it.condition_note != null ? String(it.condition_note) : "",
      };
    }
    setDrafts(next);
  }, [inspection]);

  if (!modal) return null;

  // Document semantics follow the inspection type (backend: InventoryInspection.type,
  // one row per tenancy per type). move_in is an Übergabe, move_out an Abnahme —
  // two legally distinct documents from ONE pipeline. Mirrors the backend maps in
  // inspection_protocol_pdf_data_builder (protocol_title_de / protocol_event_label_de).
  const isMoveOut = invType === "move_out";
  const eventLabel = isMoveOut ? "Abnahme" : "Übergabe";
  const protocolLabel = isMoveOut ? "Abnahmeprotokoll" : "Übergabeprotokoll";

  const title = `${eventLabel} — Checkliste`;
  const items = Array.isArray(inspection?.items) ? inspection.items : [];
  const asgList = Array.isArray(unitAssignments) ? unitAssignments : [];

  // Target room resolution (same precedence as backend generation):
  //   1. inspection.room_id (new inspections store it)
  //   2. tenancy.room_id (legacy inspections have inspection.room_id NULL but the
  //      tenancy still knows its room — filter by it, do NOT show all)
  //   3. neither available → emergency legacy mode → show all rows
  const targetRoomId = normRoomId(inspection?.room_id) ?? normRoomId(tenancy?.room_id);

  // Phase-1 room scope, identical to the PDF builder (5.4a). The room id is now
  // resolved snapshot-first: a snapshot row uses its frozen snapshot_room_id; a
  // legacy row (snapshot NULL) falls back to the live assignment.room_id.
  //   - no target room                       → keep all (legacy/unit-wide)
  //   - legacy row + assignment unresolvable → keep (never hide blindly)
  //   - room id null (shared/common)         → keep
  //   - room id == target                    → keep
  //   - else                                 → hide (foreign room)
  const visibleItems = items.filter((it) => {
    if (!targetRoomId) return true; // emergency legacy mode: no target room → show all
    const asg = asgList.find((a) => String(a.id) === String(it.assignment_id));
    const hasSnapshot = hasItemSnapshot(it);
    if (!hasSnapshot && !asg) return true; // unresolved legacy → never hide blindly
    const roomIdForFilter = hasSnapshot
      ? normRoomId(it.snapshot_room_id)
      : normRoomId(asg?.room_id);
    return roomIdForFilter === null || roomIdForFilter === targetRoomId; // shared OR target
  });

  // Progress counts — denominator is visibleItems (Phase-1 room/shared filtered),
  // NOT all DB inspection items. Critical for legacy inspections whose DB rows
  // may still include other rooms. "checked" = anything other than not_checked
  // (not_applicable counts as checked); "open" = not_checked; "problem" =
  // damaged or missing.
  const counts = { total: visibleItems.length, open: 0, damaged: 0, missing: 0 };
  for (const it of visibleItems) {
    const s = statusOf(it);
    if (s === "not_checked") counts.open += 1;
    else if (s === "damaged") counts.damaged += 1;
    else if (s === "missing") counts.missing += 1;
  }
  const checkedCount = counts.total - counts.open;

  // Phase-4 lock: finalized_at is the single source of truth. Once set, all
  // content controls go read-only and the finalize button is replaced by a badge.
  const isFinalized = Boolean(inspection?.finalized_at);

  // Non-blocking finalize warnings (Phase-4 product rules): unchecked positions
  // and damaged/missing positions without a photo only warn — they never block.
  const hasOpenItems = counts.open > 0;
  const hasProblemWithoutPhoto = visibleItems.some((it) => {
    const s = statusOf(it);
    if (s !== "damaged" && s !== "missing") return false;
    return !(Array.isArray(it.photos) && it.photos.length > 0);
  });

  const handleFinalizeClick = () => {
    if (isFinalized || finalizing) return;
    const ok = window.confirm(
      `Diese ${eventLabel} wird abgeschlossen und danach gesperrt. Status, Notizen und Fotos können danach nicht mehr geändert werden. Fortfahren?`
    );
    if (ok) onFinalize?.();
  };

  // Phase-5 PDF controls (only after finalized).
  const hasPdf = Boolean(inspection?.protocol_pdf_object_key);

  const handleGeneratePdfClick = () => {
    if (generatingProtocolPdf) return;
    onGenerateProtocolPdf?.(false); // first generation — no confirmation
  };

  const handleRegeneratePdfClick = () => {
    if (generatingProtocolPdf) return;
    const ok = window.confirm("Das bestehende PDF wird neu erzeugt. Fortfahren?");
    if (ok) onGenerateProtocolPdf?.(true);
  };

  const handleOpenPdfClick = () => {
    if (openingProtocolPdf) return;
    onOpenProtocolPdf?.();
  };

  // ── Dual signature (ÜP-S3) ──────────────────────────────────────────
  const isSigned = Boolean(inspection?.signed_at);
  const hasSignedPdf = Boolean(inspection?.signed_pdf_object_key);

  const openSignPanel = () => {
    setSignaturePlace(inspection?.signature_place || "");
    // Prefill both signer names from data Vantio already holds. These are a
    // convenience only: both fields stay editable, and whatever the signer
    // finally types is what gets persisted as the signature snapshot
    // (signed_by_name / management_signed_by_name).
    //   - tenant: inspection.tenant_name, resolved server-side via
    //     tenancy -> tenant (inventory_service._tenant_names_for_inspections).
    //   - management: the logged-in admin's full_name, passed in by the parent
    //     page (this component stays props-in / no-API).
    setTenantSignerName(inspection?.tenant_name || tenancy?.tenant_name || "");
    setManagementSignerName(currentUserName || "");
    setSignErrors({});
    tenantSigPadRef.current?.clear();
    managementSigPadRef.current?.clear();
    setSignOpen(true);
  };

  const closeSignPanel = () => {
    if (signing) return;
    setSignErrors({});
    setSignOpen(false);
  };

  const placeTrim = signaturePlace.trim();
  const tenantNameTrim = tenantSignerName.trim();
  const mgmtNameTrim = managementSignerName.trim();

  // The submit button is disabled ONLY for states where a click cannot do
  // anything useful (request in flight, not finalized yet, already signed).
  // Missing required fields deliberately do NOT disable it: a greyed-out button
  // cannot explain itself, so those are surfaced as inline errors on click.
  const signBlocked = Boolean(signing) || !isFinalized || isSigned;

  const clearSignError = (field) => {
    setSignErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Signature pads report emptiness on every stroke — a first stroke clears the
  // pad's error without needing any extra SignaturePad API. Emptiness itself is
  // read from the pad at submit time, so it needs no mirrored state here.
  const handleTenantSigEmptyChange = (empty) => {
    if (!empty) clearSignError("tenantSignature");
  };
  const handleManagementSigEmptyChange = (empty) => {
    if (!empty) clearSignError("managementSignature");
  };

  const focusSignField = (field) => {
    const el = {
      signaturePlace: signaturePlaceRef.current,
      tenantSignerName: tenantNameRef.current,
      tenantSignature: tenantSigBoxRef.current,
      managementSignerName: managementNameRef.current,
      managementSignature: managementSigBoxRef.current,
    }[field];
    if (!el) return;
    el.scrollIntoView?.({ behavior: "smooth", block: "center" });
    el.focus?.();
  };

  // Field order = visual order, so "first invalid" is also the topmost one.
  const SIGN_FIELD_ORDER = [
    "signaturePlace",
    "tenantSignerName",
    "tenantSignature",
    "managementSignerName",
    "managementSignature",
  ];

  // Defensive validation runs BEFORE the waiting tab opens (the window.open lives
  // in the hook's onSignDual, invoked synchronously only after all checks pass) —
  // so a failed validation never leaves an empty tab behind.
  const handleSignSubmit = async () => {
    if (signing) return;
    // Technical blockers: not a form error the user can fix here, so they stay
    // toasts (and the button is disabled for them anyway).
    if (!isFinalized) {
      toast.error(`Die ${eventLabel} muss zuerst abgeschlossen werden.`);
      return;
    }
    if (isSigned) {
      toast.error(`${protocolLabel} ist bereits unterzeichnet.`);
      return;
    }

    // Collect ALL field errors at once so the user sees every open point in one
    // pass instead of discovering them one toast at a time.
    const tenantSigMissing =
      !tenantSigPadRef.current || tenantSigPadRef.current.isEmpty();
    const mgmtSigMissing =
      !managementSigPadRef.current || managementSigPadRef.current.isEmpty();
    const tenantDataUrl = tenantSigMissing
      ? null
      : tenantSigPadRef.current.toDataUrl();
    const managementDataUrl = mgmtSigMissing
      ? null
      : managementSigPadRef.current.toDataUrl();

    const errors = {};
    if (!placeTrim) errors.signaturePlace = `Bitte Ort der ${eventLabel} eingeben.`;
    if (!tenantNameTrim) {
      errors.tenantSignerName = "Bitte Namen der Mieterin/des Mieters eingeben.";
    }
    if (!tenantDataUrl) errors.tenantSignature = "Bitte Mieter-Signatur erfassen.";
    if (!mgmtNameTrim) {
      errors.managementSignerName = "Bitte Namen der Verwaltung/Vertretung eingeben.";
    }
    if (!managementDataUrl) {
      errors.managementSignature =
        "Bitte Verwaltung/Vertretung unterschreiben lassen.";
    }

    if (Object.keys(errors).length > 0) {
      setSignErrors(errors);
      const first = SIGN_FIELD_ORDER.find((f) => errors[f]);
      if (first) focusSignField(first);
      return; // no API call while anything is missing
    }

    setSignErrors({});
    // All valid → onSignDual opens the waiting tab synchronously, then awaits.
    const ok = await onSignDual?.({
      signature_place: placeTrim,
      signed_by_name: tenantNameTrim,
      signature_data_url: tenantDataUrl,
      management_signed_by_name: mgmtNameTrim,
      management_signature_data_url: managementDataUrl,
    });
    if (ok) {
      setSignOpen(false);
      toast.success(`${protocolLabel} wurde unterzeichnet.`);
    }
  };

  const handleOpenSignedPdfClick = () => {
    if (openingSignedPdf) return;
    onOpenSignedPdf?.();
  };

  // Phase-2 status filter applied AFTER the Phase-1 room/shared filter.
  const activeFilter =
    STATUS_FILTERS.find((f) => f.key === statusFilter) || STATUS_FILTERS[0];
  const displayedItems = visibleItems.filter((it) => activeFilter.match(statusOf(it)));

  // Phase-6 room grouping (display-only, no persistence, safe when finalized):
  // target-room items first, then shared/common, then any foreign/unassigned
  // room. Uses the same snapshot-first resolveItemDisplay as the row render, so
  // finalized protocols group by their frozen room, not live assignments.
  // Stable within each group — the original displayedItems order is preserved.
  const GROUP_LABELS = {
    1: "Zimmer / privater Bereich",
    2: "Gemeinschaftlich genutzt",
    3: "Sonstiges",
  };
  const displayGroupOf = (it) => {
    const asg = asgList.find((a) => String(a.id) === String(it.assignment_id));
    const roomId = resolveItemDisplay(it, asg).roomId; // normalized: string | null
    // Emergency legacy mode (targetRoomId null) has no private group; a null
    // room id is shared/common (group 2), never mistaken for a private room.
    if (targetRoomId && roomId === targetRoomId) return 1;
    if (roomId === null) return 2;
    return 3;
  };
  const groupedDisplayedItems = displayedItems
    .map((it, index) => ({ it, index, group: displayGroupOf(it) }))
    .sort((a, b) => (a.group !== b.group ? a.group - b.group : a.index - b.index));
  const groupSizes = groupedDisplayedItems.reduce((acc, g) => {
    acc[g.group] = (acc[g.group] || 0) + 1;
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inspection-modal-title"
    >
      <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-[14px] border border-black/10 bg-white shadow-xl dark:border-white/[0.06] dark:bg-[#141824]">
        <div className="flex items-start justify-between gap-3 border-b border-black/10 px-5 py-4 dark:border-white/[0.06]">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4
                id="inspection-modal-title"
                className="m-0 text-lg font-semibold text-[#0f172a] dark:text-[#eef2ff]"
              >
                {title}
              </h4>
              {isFinalized ? (
                <span className="inline-flex items-center rounded-full bg-slate-200 px-2.5 py-1 text-[11px] font-semibold text-[#0f172a] dark:bg-white/[0.12] dark:text-[#eef2ff]">
                  🔒 Abgeschlossen / Gesperrt
                </span>
              ) : null}
            </div>
            {unitLabelShort ? (
              <p className="mt-1 text-[12px] text-[#64748b] dark:text-[#93a4bf]">{unitLabelShort}</p>
            ) : null}
            {isFinalized ? (
              <p className="mt-1 text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                {`Abgeschlossen am ${formatFinalizedAt(inspection?.finalized_at)}`}
                {inspection?.finalized_by ? ` · ${String(inspection.finalized_by)}` : ""}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-black/10 px-3 py-1.5 text-[13px] text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
          >
            Schließen
          </button>
        </div>
        <div className="max-h-[calc(92vh-88px)] overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="m-0 text-[13px] text-[#64748b] dark:text-[#93a4bf]">Lade …</p>
          ) : err ? (
            <p className="m-0 text-sm text-[#f87171]">{err}</p>
          ) : visibleItems.length === 0 ? (
            <p className="m-0 text-[13px] text-[#64748b] dark:text-[#93a4bf]">
              Keine Inventar-Zuordnungen für dieses Zimmer oder gemeinschaftliches
              Inventar — keine Checklisten-Positionen.
            </p>
          ) : (
            <>
              {/* Progress summary — denominator is visibleItems (Phase-1 filtered). */}
              <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-[#0f172a] dark:bg-white/[0.06] dark:text-[#eef2ff]">
                  {`Geprüft: ${checkedCount} / ${counts.total}`}
                </span>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[#64748b] dark:bg-white/[0.06] dark:text-[#93a4bf]">
                  {`Offen: ${counts.open}`}
                </span>
                <span className="rounded-full bg-orange-100 px-2.5 py-1 text-orange-700 dark:bg-orange-500/[0.12] dark:text-orange-300">
                  {`Beschädigt: ${counts.damaged}`}
                </span>
                <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-700 dark:bg-red-500/[0.12] dark:text-red-300">
                  {`Fehlt: ${counts.missing}`}
                </span>
              </div>
              {/* Status filter chips — applied to visibleItems after the room/shared filter. */}
              <div className="mb-3 flex flex-wrap gap-2">
                {STATUS_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setStatusFilter(f.key)}
                    aria-pressed={statusFilter === f.key}
                    className={`rounded-full border px-3 py-1 text-[12px] ${
                      statusFilter === f.key
                        ? "border-[#5b8cff] bg-[#5b8cff] text-white"
                        : "border-black/10 bg-white text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:bg-transparent dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {displayedItems.length === 0 ? (
                <p className="m-0 text-[13px] text-[#64748b] dark:text-[#93a4bf]">
                  Keine Positionen für diesen Filter.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] border-separate border-spacing-0 text-left text-[13px] text-[#0f172a] dark:text-[#eef2ff]">
                    <thead>
                  <tr className="border-b border-black/10 text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:border-white/[0.06] dark:text-[#6b7a9a]">
                    <th className="pb-2 pr-3">Artikel</th>
                    <th className="pb-2 pr-3">Einheit / Raum</th>
                    <th className="pb-2 pr-3 text-right">Menge</th>
                    <th className="pb-2 pr-3">Status</th>
                    <th className="pb-2 pr-3">Notiz</th>
                    <th className="pb-2 pr-0 text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedDisplayedItems.map(({ it, group }, gi) => {
                    const prevGroup = gi > 0 ? groupedDisplayedItems[gi - 1].group : null;
                    const showGroupHeader = group !== prevGroup;
                    const asg = asgList.find((a) => String(a.id) === String(it.assignment_id));
                    // Phase 5.4b: snapshot-first (frozen) with live fallback for legacy.
                    const disp = resolveItemDisplay(it, asg);
                    const name = disp.name;
                    // room_id null → shared/common label; otherwise the room name.
                    const room =
                      disp.roomId === null
                        ? "Gemeinschaftlich genutzt"
                        : disp.roomName || "—";
                    const qty = disp.quantity;
                    const d = drafts[it.id] || {
                      check_status: String(it.check_status || "not_checked").toLowerCase(),
                      condition_note: it.condition_note != null ? String(it.condition_note) : "",
                    };
                    const statusClass = inspectionCheckStatusTextClass(d.check_status);
                    const photos = Array.isArray(it.photos) ? it.photos : [];
                    const isUploading = uploadingPhotoItemId === it.id;
                    const photoRecommended =
                      d.check_status === "damaged" || d.check_status === "missing";
                    return (
                      <React.Fragment key={it.id}>
                      {showGroupHeader ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:text-[#6b7a9a]"
                          >
                            {`${GROUP_LABELS[group]} · ${groupSizes[group]}`}
                          </td>
                        </tr>
                      ) : null}
                      <tr className="border-b border-black/5 dark:border-white/[0.04]">
                        <td className="py-3 pr-3 align-top font-medium">{name}</td>
                        <td className="py-3 pr-3 align-top text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                          {room}
                        </td>
                        <td className="py-3 pr-3 align-top text-right tabular-nums">{qty}</td>
                        <td className="py-3 pr-3 align-top">
                          <select
                            value={d.check_status}
                            disabled={isFinalized}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [it.id]: { ...d, check_status: e.target.value },
                              }))
                            }
                            className={`w-full min-w-[140px] rounded-lg border border-black/10 bg-slate-50 px-2 py-1.5 text-[13px] disabled:opacity-60 dark:border-white/[0.08] dark:bg-[#111520] ${statusClass}`}
                          >
                            <option value="ok">OK</option>
                            <option value="damaged">Beschädigt</option>
                            <option value="missing">Fehlt</option>
                            <option value="not_checked">Nicht geprüft</option>
                            <option value="not_applicable">Nicht relevant</option>
                          </select>
                        </td>
                        <td className="py-3 pr-3 align-top">
                          <input
                            type="text"
                            value={d.condition_note}
                            disabled={isFinalized}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [it.id]: { ...d, condition_note: e.target.value },
                              }))
                            }
                            placeholder="optional"
                            className="w-full min-w-[120px] rounded-lg border border-black/10 bg-slate-50 px-2 py-1.5 text-[13px] disabled:opacity-60 dark:border-white/[0.08] dark:bg-[#111520]"
                          />
                        </td>
                        <td className="py-3 align-top text-right">
                          {isFinalized ? (
                            <span className="text-[12px] text-[#94a3b8]">Gesperrt</span>
                          ) : (
                            <button
                              type="button"
                              disabled={savingItemId === it.id}
                              onClick={() =>
                                onSaveRow(it.id, d.check_status, d.condition_note)
                              }
                              className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50 ${
                                savedItemId === it.id ? "bg-emerald-600" : "bg-[#5b8cff]"
                              }`}
                            >
                              {savingItemId === it.id
                                ? "…"
                                : savedItemId === it.id
                                  ? "Gespeichert ✓"
                                  : "Speichern"}
                            </button>
                          )}
                        </td>
                      </tr>
                      <tr className="border-b border-black/5 dark:border-white/[0.04]">
                        <td colSpan={6} className="pb-3 pr-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {isFinalized ? null : (
                              <label
                                className={`inline-flex cursor-pointer items-center rounded-lg border border-black/10 bg-slate-50 px-2.5 py-1 text-[12px] text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:bg-[#111520] dark:text-[#eef2ff] dark:hover:bg-white/[0.04] ${
                                  isUploading ? "pointer-events-none opacity-50" : ""
                                }`}
                              >
                                Foto hinzufügen
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  multiple
                                  disabled={isUploading}
                                  className="hidden"
                                  onChange={(e) => {
                                    const files = e.target.files;
                                    if (files && files.length) onUploadPhotos?.(it.id, files);
                                    e.target.value = "";
                                  }}
                                />
                              </label>
                            )}
                            {isUploading ? (
                              <span className="text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                                Wird hochgeladen …
                              </span>
                            ) : null}
                            {photoRecommended ? (
                              <span className="text-[12px] font-medium text-amber-500">
                                Foto empfohlen
                              </span>
                            ) : null}
                            {photos.length === 0 && !isUploading ? (
                              <span className="text-[12px] text-[#94a3b8]">Keine Fotos</span>
                            ) : null}
                          </div>
                          {photos.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {photos.map((p) => (
                                <div key={p.id} className="relative">
                                  <a href={p.original_url} target="_blank" rel="noreferrer">
                                    <img
                                      src={p.thumb_url}
                                      alt={p.caption || "Foto"}
                                      className="h-16 w-16 rounded-md border border-black/10 object-cover dark:border-white/[0.12]"
                                    />
                                  </a>
                                  {isFinalized ? null : (
                                    <button
                                      type="button"
                                      aria-label="Foto löschen"
                                      onClick={() => onDeletePhoto?.(it.id, p.id)}
                                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold leading-none text-white"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
                  </table>
                </div>
              )}
              {isFinalized ? null : (
                <div className="mt-4 border-t border-black/10 pt-4 dark:border-white/[0.06]">
                  {hasOpenItems ? (
                    <p className="mb-2 text-[12px] text-amber-600 dark:text-amber-400">
                      Es gibt noch ungeprüfte Positionen. Du kannst die {eventLabel}
                      trotzdem abschließen.
                    </p>
                  ) : null}
                  {hasProblemWithoutPhoto ? (
                    <p className="mb-2 text-[12px] text-amber-600 dark:text-amber-400">
                      Es gibt beschädigte oder fehlende Positionen ohne Foto. Du
                      kannst die {eventLabel} trotzdem abschließen.
                    </p>
                  ) : null}
                  <button
                    type="button"
                    disabled={finalizing}
                    onClick={handleFinalizeClick}
                    className="rounded-lg bg-[#0f172a] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50 dark:bg-[#eef2ff] dark:text-[#0f172a]"
                  >
                    {finalizing ? "Wird abgeschlossen …" : `${eventLabel} abschließen`}
                  </button>
                </div>
              )}
              {isFinalized ? (
                <div className="mt-4 border-t border-black/10 pt-4 dark:border-white/[0.06]">
                  <div className="mb-1 text-[12px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                    {protocolLabel}
                  </div>

                  {isSigned ? (
                    /* Signed → the signed PDF is the authoritative version. No
                       regenerate of the normal PDF (backend blocks it anyway). */
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-50/60 p-3 dark:border-emerald-400/20 dark:bg-emerald-950/20">
                      <div className="mb-2 text-[12px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                        Unterzeichnet
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px] text-[#0f172a] dark:text-[#eef2ff]">
                        <div className="col-span-2">
                          <span className="text-[#64748b] dark:text-[#93a4bf]">Ort: </span>
                          {inspection?.signature_place || "—"}
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold text-[#64748b] dark:text-[#93a4bf]">Mieter/in</div>
                          <div>{inspection?.signed_by_name || "—"}</div>
                          <div className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
                            {inspection?.signed_at ? formatFinalizedAt(inspection.signed_at) : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] font-semibold text-[#64748b] dark:text-[#93a4bf]">Verwaltung/Vertretung</div>
                          <div>{inspection?.management_signed_by_name || "—"}</div>
                          <div className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
                            {inspection?.management_signed_at ? formatFinalizedAt(inspection.management_signed_at) : "—"}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {hasSignedPdf ? (
                          <button
                            type="button"
                            disabled={openingSignedPdf}
                            onClick={handleOpenSignedPdfClick}
                            className="rounded-lg bg-[#5b8cff] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
                          >
                            {openingSignedPdf ? "Wird geöffnet …" : "Unterschriebenes PDF öffnen"}
                          </button>
                        ) : (
                          <span className="text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                            Unterschriebenes PDF ist noch nicht verfügbar.
                          </span>
                        )}
                        {hasPdf ? (
                          <button
                            type="button"
                            disabled={openingProtocolPdf}
                            onClick={handleOpenPdfClick}
                            className="rounded-lg border border-black/10 px-4 py-2 text-[13px] font-semibold text-[#0f172a] disabled:opacity-50 dark:border-white/[0.12] dark:text-[#eef2ff]"
                          >
                            {openingProtocolPdf ? "Wird geöffnet …" : "Unsigniertes PDF öffnen"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Normal protocol PDF (finalized, not yet signed). */}
                      {hasPdf ? (
                        <>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={openingProtocolPdf || generatingProtocolPdf}
                              onClick={handleOpenPdfClick}
                              className="rounded-lg bg-[#5b8cff] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
                            >
                              {openingProtocolPdf ? "Wird geöffnet …" : "PDF öffnen"}
                            </button>
                            <button
                              type="button"
                              disabled={generatingProtocolPdf || openingProtocolPdf}
                              onClick={handleRegeneratePdfClick}
                              className="rounded-lg border border-black/10 px-4 py-2 text-[13px] font-semibold text-[#0f172a] disabled:opacity-50 dark:border-white/[0.12] dark:text-[#eef2ff]"
                            >
                              {generatingProtocolPdf ? "Wird generiert …" : "PDF neu generieren"}
                            </button>
                          </div>
                          {inspection?.protocol_pdf_generated_at ? (
                            <p className="mt-1 text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                              {`PDF generiert am ${formatFinalizedAt(inspection.protocol_pdf_generated_at)}`}
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={generatingProtocolPdf}
                          onClick={handleGeneratePdfClick}
                          className="rounded-lg bg-[#5b8cff] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
                        >
                          {generatingProtocolPdf ? "Wird generiert …" : "PDF generieren"}
                        </button>
                      )}

                      {/* Dual signature */}
                      <div className="mt-4 border-t border-black/10 pt-4 dark:border-white/[0.06]">
                        {!signOpen ? (
                          <button
                            type="button"
                            onClick={openSignPanel}
                            className="rounded-lg bg-[#0f172a] px-4 py-2 text-[13px] font-semibold text-white dark:bg-[#eef2ff] dark:text-[#0f172a]"
                          >
                            {protocolLabel} unterschreiben
                          </button>
                        ) : (
                          <div className="flex flex-col gap-3">
                            <div className="text-[13px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                              {protocolLabel} unterschreiben
                            </div>
                            <div className="rounded-lg border border-amber-500/40 bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-800 dark:border-amber-400/30 dark:bg-amber-950/30 dark:text-amber-300">
                              Nach dem Unterschreiben kann das Protokoll nicht mehr geändert werden.
                              Das unterzeichnete PDF ist danach der maßgebliche Stand.
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <div>
                                <label
                                  htmlFor="sign-place"
                                  className="mb-1 block text-[12px] text-[#64748b] dark:text-[#93a4bf]"
                                >
                                  Ort der {eventLabel}{" "}
                                  <span aria-hidden="true" className="text-red-600 dark:text-red-400">*</span>
                                  <span className="sr-only">(Pflichtfeld)</span>
                                </label>
                                <input
                                  id="sign-place"
                                  ref={signaturePlaceRef}
                                  type="text"
                                  value={signaturePlace}
                                  disabled={signing}
                                  aria-required="true"
                                  aria-invalid={signErrors.signaturePlace ? "true" : undefined}
                                  aria-describedby={
                                    signErrors.signaturePlace ? "sign-place-error" : undefined
                                  }
                                  onChange={(e) => {
                                    setSignaturePlace(e.target.value);
                                    clearSignError("signaturePlace");
                                  }}
                                  className={`w-full rounded-lg border bg-slate-50 px-2 py-1.5 text-[13px] disabled:opacity-60 dark:bg-[#111520] dark:text-[#eef2ff] ${
                                    signErrors.signaturePlace
                                      ? "border-red-500 dark:border-red-400"
                                      : "border-black/10 dark:border-white/[0.08]"
                                  }`}
                                />
                                {signErrors.signaturePlace ? (
                                  <p
                                    id="sign-place-error"
                                    className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400"
                                  >
                                    {signErrors.signaturePlace}
                                  </p>
                                ) : null}
                              </div>
                              <div>
                                <label
                                  htmlFor="sign-date"
                                  className="mb-1 block text-[12px] text-[#64748b] dark:text-[#93a4bf]"
                                >
                                  Datum der {eventLabel}
                                </label>
                                <input
                                  id="sign-date"
                                  type="text"
                                  readOnly
                                  value={formatDateOnly(inspection?.inspection_date)}
                                  className="w-full rounded-lg border border-black/10 bg-slate-100 px-2 py-1.5 text-[13px] text-[#64748b] dark:border-white/[0.08] dark:bg-[#0f1320] dark:text-[#93a4bf]"
                                />
                                <p className="mt-1 text-[11px] text-[#94a3b8]">Datum aus dem Protokolldatum.</p>
                              </div>
                            </div>
                            <p className="text-[11px] text-[#94a3b8]">
                              Für eine bessere Unterschrift das Gerät quer halten.
                            </p>

                            {/* Mieter/in */}
                            <div className="rounded-lg border border-black/10 p-3 dark:border-white/[0.08]">
                              <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:text-[#6b7a9a]">Mieter/in</div>
                              <label
                                htmlFor="sign-tenant-name"
                                className="mb-1 block text-[12px] text-[#64748b] dark:text-[#93a4bf]"
                              >
                                Name Mieter/in{" "}
                                <span aria-hidden="true" className="text-red-600 dark:text-red-400">*</span>
                                <span className="sr-only">(Pflichtfeld)</span>
                              </label>
                              <input
                                id="sign-tenant-name"
                                ref={tenantNameRef}
                                type="text"
                                value={tenantSignerName}
                                disabled={signing}
                                aria-required="true"
                                aria-invalid={signErrors.tenantSignerName ? "true" : undefined}
                                aria-describedby={
                                  signErrors.tenantSignerName ? "sign-tenant-name-error" : undefined
                                }
                                onChange={(e) => {
                                  setTenantSignerName(e.target.value);
                                  clearSignError("tenantSignerName");
                                }}
                                className={`w-full rounded-lg border bg-slate-50 px-2 py-1.5 text-[13px] disabled:opacity-60 dark:bg-[#111520] dark:text-[#eef2ff] ${
                                  signErrors.tenantSignerName
                                    ? "border-red-500 dark:border-red-400"
                                    : "border-black/10 dark:border-white/[0.08]"
                                }`}
                              />
                              {signErrors.tenantSignerName ? (
                                <p
                                  id="sign-tenant-name-error"
                                  className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400"
                                >
                                  {signErrors.tenantSignerName}
                                </p>
                              ) : null}
                              <div
                                ref={tenantSigBoxRef}
                                tabIndex={-1}
                                aria-invalid={signErrors.tenantSignature ? "true" : undefined}
                                aria-describedby={
                                  signErrors.tenantSignature ? "sign-tenant-name-sig-error" : undefined
                                }
                                className={`mt-2 rounded-lg ${
                                  signErrors.tenantSignature
                                    ? "ring-2 ring-red-500 dark:ring-red-400"
                                    : ""
                                }`}
                              >
                                <SignaturePad ref={tenantSigPadRef} height={180} onEmptyChange={handleTenantSigEmptyChange} />
                              </div>
                              {signErrors.tenantSignature ? (
                                <p
                                  id="sign-tenant-name-sig-error"
                                  className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400"
                                >
                                  {signErrors.tenantSignature}
                                </p>
                              ) : null}
                              <button
                                type="button"
                                disabled={signing}
                                onClick={() => tenantSigPadRef.current?.clear()}
                                className="mt-2 rounded-lg border border-black/10 px-3 py-1.5 text-[12px] text-[#0f172a] disabled:opacity-50 dark:border-white/[0.12] dark:text-[#eef2ff]"
                              >
                                Mieter-Signatur zurücksetzen
                              </button>
                            </div>

                            {/* Verwaltung/Vertretung */}
                            <div className="rounded-lg border border-black/10 p-3 dark:border-white/[0.08]">
                              <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:text-[#6b7a9a]">Verwaltung/Vertretung</div>
                              <label
                                htmlFor="sign-mgmt-name"
                                className="mb-1 block text-[12px] text-[#64748b] dark:text-[#93a4bf]"
                              >
                                Name Verwaltung/Vertretung{" "}
                                <span aria-hidden="true" className="text-red-600 dark:text-red-400">*</span>
                                <span className="sr-only">(Pflichtfeld)</span>
                              </label>
                              <input
                                id="sign-mgmt-name"
                                ref={managementNameRef}
                                type="text"
                                value={managementSignerName}
                                disabled={signing}
                                aria-required="true"
                                aria-invalid={signErrors.managementSignerName ? "true" : undefined}
                                aria-describedby={
                                  signErrors.managementSignerName ? "sign-mgmt-name-error" : undefined
                                }
                                onChange={(e) => {
                                  setManagementSignerName(e.target.value);
                                  clearSignError("managementSignerName");
                                }}
                                className={`w-full rounded-lg border bg-slate-50 px-2 py-1.5 text-[13px] disabled:opacity-60 dark:bg-[#111520] dark:text-[#eef2ff] ${
                                  signErrors.managementSignerName
                                    ? "border-red-500 dark:border-red-400"
                                    : "border-black/10 dark:border-white/[0.08]"
                                }`}
                              />
                              {signErrors.managementSignerName ? (
                                <p
                                  id="sign-mgmt-name-error"
                                  className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400"
                                >
                                  {signErrors.managementSignerName}
                                </p>
                              ) : null}
                              <div
                                ref={managementSigBoxRef}
                                tabIndex={-1}
                                aria-invalid={signErrors.managementSignature ? "true" : undefined}
                                aria-describedby={
                                  signErrors.managementSignature ? "sign-mgmt-name-sig-error" : undefined
                                }
                                className={`mt-2 rounded-lg ${
                                  signErrors.managementSignature
                                    ? "ring-2 ring-red-500 dark:ring-red-400"
                                    : ""
                                }`}
                              >
                                <SignaturePad ref={managementSigPadRef} height={180} onEmptyChange={handleManagementSigEmptyChange} />
                              </div>
                              {signErrors.managementSignature ? (
                                <p
                                  id="sign-mgmt-name-sig-error"
                                  className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400"
                                >
                                  {signErrors.managementSignature}
                                </p>
                              ) : null}
                              <button
                                type="button"
                                disabled={signing}
                                onClick={() => managementSigPadRef.current?.clear()}
                                className="mt-2 rounded-lg border border-black/10 px-3 py-1.5 text-[12px] text-[#0f172a] disabled:opacity-50 dark:border-white/[0.12] dark:text-[#eef2ff]"
                              >
                                Verwaltungs-Signatur zurücksetzen
                              </button>
                            </div>

                            <p className="text-[11px] text-[#94a3b8]">
                              Bitte Ort, beide Namen und beide Unterschriften
                              vollständig erfassen.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={signing}
                                onClick={closeSignPanel}
                                className="rounded-lg border border-black/10 px-4 py-2 text-[13px] font-semibold text-[#0f172a] disabled:opacity-50 dark:border-white/[0.12] dark:text-[#eef2ff]"
                              >
                                Abbrechen
                              </button>
                              <button
                                type="button"
                                disabled={signBlocked}
                                onClick={handleSignSubmit}
                                className="rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                {signing
                                  ? "Wird unterzeichnet …"
                                  : `${eventLabel} unterzeichnen & PDF erstellen`}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
